/* =========================================================================
   LAYER'D Toolkit - THE PRINTED SERVING PLAN, composer (r191, 2026-08-31).

   Builds the print-only sheet (#ldcPlan) from the plan the page just
   calculated, and - on the free tier - the watermarked SAMPLE preview of
   that same sheet. Load AFTER learn-cake-core.js and learn-serving.js.

   Contract - learn-serving.js publishes what it rendered:

     window.LAYERD.servingPlan = {
       tiers:    [{ size, height, shape, faux }],   // as passed to renderStack
       total:    107,        // servings this cake gives
       target:   100,        // serving target, or null in tiers mode
       guests:   100,        // headcount behind that target, or null
       occasion: 'wedding'
     }

   Nothing is read out of the results DOM: the sheet is composed from the
   same data the screen drew, so paper and screen can never disagree. The
   cake is CORE.renderStack(tiers, { florals: true }) - the identical call
   the screen makes, so the printed drawing IS the screen drawing.

   TIERS: Pro prints it clean. Free gets the whole sheet too, watermarked
   SAMPLE - the free tier demonstrates depth, it does not hide the thing
   (and a watermarked sheet is a far better argument for Pro than a locked
   button). The gate is a paywall door, not a secret: every number on this
   sheet is the visitor's own, already on their screen, so nothing
   premium ships in this asset.
   ========================================================================= */
(function () {
  'use strict';

  var STUDIO_KEYS = {
    name: 'layerd-studio-name',
    contact: 'layerd-studio-contact',
    logo: 'layerd-studio-logo'
  };
  var FALLBACK_BRAND = 'LAYER’D CAKES';
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  /* the sheet talks like a document, not a readout: small counts are
     spelled (the design file's own line is "Seven in hand on a guest count
     of 100."). Above twelve, digits read faster than words. */
  var WORDS = ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven',
    'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];

  function core() { return (window.LAYERD && window.LAYERD.cakeCore) || null; }
  function lsGet(k) { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* the gate's tier, off the subnav - the same read every other analytics
     and door in this tool uses. Anything unrecognised is a visitor. */
  function tier() {
    var host = document.querySelector('[data-lh-tier]');
    var t = host && host.getAttribute('data-lh-tier');
    return (t === 'free' || t === 'premium' || t === 'visitor') ? t : 'visitor';
  }
  function isPro() { return tier() === 'premium'; }

  function ga(evt, params) {
    try {
      if (window.lhTrack) {
        params = params || {};
        params.tool_name = 'serving';
        params.user_tier = tier();
        window.lhTrack(evt, params);
      }
    } catch (e) { /* analytics must never break a tool */ }
  }

  /* ------------------------------------------------------------------ data */

  /* the booking, if one is open. Name and date are INDEPENDENTLY optional -
     a file started from an inquiry often has a name and no date yet, and
     the sheet must not print a stray separator for the half that is
     missing. */
  function client() {
    var b = null;
    try {
      var F = window.LdCakeFile;
      b = (F && F.getActiveBooking && F.getActiveBooking()) || null;
    } catch (e) { b = null; }
    if (!b) return null;
    var name = (b.name || '').trim();
    var date = fmtDate(b.date);
    if (!name && !date) return null;          /* file with neither: omit the block */
    return { name: name, date: date };
  }

  /* the file stores an ISO date; a client-facing sheet reads it long-form.
     An unparseable value prints verbatim rather than vanishing. */
  function fmtDate(raw) {
    var s = (raw || '').trim();
    if (!s) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return s;
    var mi = +m[2] - 1;
    if (!MONTHS[mi]) return s;
    return (+m[3]) + ' ' + MONTHS[mi] + ' ' + m[1];
  }

  function today() {
    var d = new Date();
    return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  function countWord(n) {
    return (n >= 1 && n <= 12) ? WORDS[n] : String(n);
  }

  /* ---- the one line that states the outcome against the target. Only a
         target makes "in hand" meaningful; tiers mode has none, so the line
         is simply absent (CSS hides an empty node).

         When "other desserts are served" is ticked the target is 85% of the
         headcount, so the two numbers are NOT the same thing and the line
         has to say both - claiming "a guest count of 85" for a 100-guest
         wedding would be a lie on a client's paper. ---- */
  function againstTarget(total, target, guests) {
    if (!(target > 0)) return '';
    var split = (guests > 0 && guests !== target);
    var basis = split
      ? 'a target of ' + target + ' servings for ' + guests + ' guests'
      : 'a guest count of ' + target;
    var d = total - target;
    if (d === 0) {
      return split ? 'Exactly the target of ' + target + ' servings for ' + guests + ' guests.'
                   : 'Exactly your guest count of ' + target + '.';
    }
    if (d > 0) return countWord(d) + ' in hand on ' + basis + '.';
    return countWord(-d) + ' short of ' + basis + '.';
  }

  /* the slice the count is actually built on. The screen disclaimer says
     both (1x2x4in, 1.5x2x4in for parties); the sheet must name the one
     that produced THIS number, never the generic one. */
  function wiltonLine(occasion) {
    return occasion === 'party'
      ? 'Servings follow the Wilton party slice, 1½″ × 2″, four inches tall.'
      : 'Servings follow the Wilton standard slice, 1″ × 2″, four inches tall.';
  }

  /* sizeLabel is the suite's shared label and ends inches with a straight
     ASCII quote, which is right for a UI and wrong on a document. Prime,
     on the sheet only - the label itself (and cm mode) is untouched. */
  function prime(s) { return String(s).replace(/"$/, '″'); }

  function tierRow(t, servings) {
    var C = core();
    var label = (C && C.SHAPE_LABEL && C.SHAPE_LABEL[t.shape || 'round']) || 'Round';
    var size = prime((C && C.sizeLabel) ? C.sizeLabel(t.size) : t.size + '″');
    var h = prime((C && C.sizeLabel) ? C.sizeLabel(t.height || 4) : (t.height || 4) + '″');
    var faux = !!t.faux;
    return '<div class="ldc-sheet__tier' + (faux ? ' ldc-sheet__tier--faux' : '') + '">' +
      '<span class="ldc-sheet__tiername">' + esc(label + ' ' + size) +
        ' · ' + esc(h) + ' tall</span>' +
      '<span class="ldc-sheet__tiern">' +
        (faux ? 'faux, serves nobody'
              : servings + (servings === 1 ? ' serving' : ' servings')) +
      '</span></div>';
  }

  /* ---------------------------------------------------------------- compose */

  /* Returns { html, mods } or null. ONE composer for both surfaces: the
     printed sheet and the free preview are the same call, so a change can
     never land on one and miss the other. */
  function compose(opts) {
    opts = opts || {};
    var plan = window.LAYERD && window.LAYERD.servingPlan;
    var C = core();
    if (!plan || !C || !plan.tiers || !plan.tiers.length) return null;

    /* renderStack sorts its own tiers biggest-first for the drawing. The
       LIST reads the other way on purpose: top tier first, the way you
       read the cake standing in front of you (design file, plate A). */
    var top = plan.tiers.slice().sort(function (a, b) { return a.size - b.size; });
    var cl = client();
    var sample = !!opts.sample;
    var brandLogo = lsGet(STUDIO_KEYS.logo);
    var mount = document.getElementById('ldcPlan');
    var brandName = lsGet(STUDIO_KEYS.name) ||
      ((mount && mount.getAttribute('data-shop-name')) || '').trim() || FALLBACK_BRAND;
    var contact = lsGet(STUDIO_KEYS.contact).trim();

    var h = '';
    h += brandLogo
      ? '<img class="ldc-sheet__logo" src="' + esc(brandLogo) + '" alt="' + esc(brandName) + '">'
      : '<div class="ldc-sheet__brand">' + esc(brandName) + '</div>';
    h += '<div class="ldc-sheet__rule"><span class="ldc-sheet__eyebrow">Cake serving plan</span></div>';

    if (cl) {
      h += '<div class="ldc-sheet__client">' +
        (cl.name ? '<span class="ldc-sheet__for">' + esc(cl.name) + '</span>' : '') +
        (cl.date ? '<span class="ldc-sheet__date">' + esc(cl.date) + '</span>' : '') +
        '</div>';
    }

    h += '<div class="ldc-sheet__cake"><div class="ldc-sheet__cakebox">' +
      C.renderStack(plan.tiers, { florals: true }) + '</div></div>';

    h += '<div class="ldc-sheet__count">' +
      '<span class="ldc-sheet__n">' + plan.total + '</span>' +
      '<span class="ldc-sheet__nlabel">Servings · drawn to scale</span>' +
      '<span class="ldc-sheet__against">' +
        esc(againstTarget(plan.total, plan.target, plan.guests)) + '</span>' +
      '</div>';

    h += '<div class="ldc-sheet__tiers">' + top.map(function (t) {
      return tierRow(t, C.servingsFor(t.shape || 'round', t.size,
        plan.occasion === 'party' ? 'party' : 'wedding', t.height || 4));
    }).join('') + '</div>';

    h += '<div class="ldc-sheet__foot">' + wiltonLine(plan.occasion) +
      (contact ? '<br>' + esc(contact) : '') +
      '<br>Prepared ' + today() + '</div>';

    if (sample) {
      h += '<div class="ldc-sheet__sample">Sample · Toolkit Pro prints this clean</div>';
      h += '<div class="ldc-sheet__mark" aria-hidden="true">SAMPLE</div>';
    }

    return {
      html: h,
      mods: (cl ? '' : ' ldc-sheet--nofile') +
            (plan.tiers.length >= 5 ? ' ldc-sheet--dense' : '')
    };
  }

  /* ------------------------------------------------------------------ print */

  /* The mount has to be a direct child of <body>: the print pass hides the
     page with `body > *` and re-shows `body > .ldc-plan`, and display:none
     on an ancestor takes this whole subtree out of the box tree - a sheet
     mounted inside .ldc-cake-calc prints BLANK at any specificity. Themes
     wrap page content, so this is the normal case, not the edge one. Done
     at load, and again in build(), rather than trusted to the template. */
  function adopt() {
    var mount = document.getElementById('ldcPlan');
    if (mount && mount.parentElement !== document.body) document.body.appendChild(mount);
    return mount;
  }

  function build() {
    var mount = adopt();
    if (!mount) return false;
    var out = compose({ sample: !isPro() });
    if (!out) {
      /* nothing calculated yet - DISARM, so Cmd-P prints the page rather
         than a guaranteed blank side of paper */
      document.body.classList.remove('ldc-plan-on');
      mount.innerHTML = '';
      return false;
    }
    mount.className = 'ldc-plan ldc-sheet' + out.mods;
    mount.innerHTML = out.html;
    document.body.classList.add('ldc-plan-on');
    pageStyle();
    return true;
  }

  /* @page cannot be scoped by a body class, and it cannot be won by
     specificity either - the last @page declared wins. learn-serving.css
     used to declare `@page { margin: 12mm }` and a theme may declare its
     own, so ours is injected into <head> at print time, after every
     stylesheet, where nothing can outrank it. Pricing r148 lost exactly
     this fight ("I always have to change it to 75%"). */
  function pageStyle() {
    if (document.getElementById('ldcPlanPage')) return;
    var st = document.createElement('style');
    st.id = 'ldcPlanPage';
    st.textContent = '@media print{@page{size:letter;margin:0}}';
    document.head.appendChild(st);
  }

  /* ------------------------------------------------------- print for real */

  var FONTS = 'https://fonts.googleapis.com/css2?family=Marcellus&' +
    'family=Hanken+Grotesk:wght@300;400;500;600&display=swap';

  /* the sheet's own stylesheet, by URL. The template stamps it; the <link>
     tag is the fallback if that attribute is ever lost. */
  function planCssHref() {
    var mount = document.getElementById('ldcPlan');
    var attr = ((mount && mount.getAttribute('data-plan-css')) || '').trim();
    if (attr) return attr;
    var l = document.querySelector('link[href*="learn-serving-plan"]');
    return (l && l.href) || '';
  }

  /* THE SHEET'S CSS, AS TEXT. r193.

     The print document used to <link> the stylesheet, and on her iPhone it
     printed with only the inline <style> applied: display:flex landed, the
     other forty rules did not, so the sheet laid itself out as a ROW -
     brand, rule, cake, count and tier list side by side across the top of
     an otherwise empty page. A written-open popup starts as about:blank and
     iOS gave the print snapshot no chance to wait for an external
     stylesheet.

     So it is INLINED, and fetched here at load rather than at print time -
     by the time she taps Print it is already in hand and the document goes
     out complete in one write. Shopify's CDN allows fetching an asset's
     text (Pricing r178 proved that, the hard way, after cssRules threw
     SecurityError on the same file). The <link> stays as a backstop for a
     failed fetch. */
  var cssText = null;
  var cssPending = null;
  function cssReady() {
    if (cssText != null) return Promise.resolve(cssText);
    if (cssPending) return cssPending;
    var href = planCssHref();
    if (!href || typeof fetch !== 'function') {
      cssText = '';
      return Promise.resolve('');
    }
    cssPending = fetch(href)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .catch(function () { return ''; })
      .then(function (t) { cssText = t || ''; return cssText; });
    return cssPending;
  }

  /* the whole print document, as a string. Its own function so it can be
     written into an iframe and inspected without a print dialog - a popup
     is unopenable in most automated browsers, which is exactly how the
     r191 bug shipped. */
  function printDoc(out, css) {
    /* a stylesheet can never contain </style>, but a truncated fetch could
       land anywhere, so the tag is neutralised rather than trusted */
    var inline = (cssText || '').replace(/<\/(style)/gi, '<\\/$1');
    return '<!doctype html><html><head><meta charset="utf-8">' +
      /* iOS lays a document with no viewport out at 980px and scales that
         to the paper. Pinning it to the sheet's own width makes the sheet
         fill the printable area instead of two thirds of it. */
      '<meta name="viewport" content="width=816">' +
      '<title>Serving plan</title>' +
      '<link rel="stylesheet" href="' + esc(FONTS) + '">' +
      (css ? '<link rel="stylesheet" href="' + esc(css) + '">' : '') +
      '<style>' + inline + '</style>' +
      '<style>@page{size:letter;margin:0}' +
      /* the sheet IS the page: exactly 816x1056 at 96dpi, and the document
         is clamped to it so a rounding pixel cannot spill a second, blank
         side of paper. */
      'html,body{margin:0;padding:0;height:1056px;overflow:hidden;background:#fff;' +
      '-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
      /* .ldc-sheet carries no display of its own (the page mount decides
         it) and visibility is stated outright - the two things a stale
         cached stylesheet could otherwise take away. Column direction is
         restated here because it is THE rule whose absence produced the
         row of debris on her phone: if the sheet's own CSS is ever missing
         again, the printout must still read top to bottom. */
      '.ldc-sheet{display:flex !important;flex-direction:column !important;' +
      'align-items:center;visibility:visible !important;' +
      'box-sizing:border-box;width:816px;height:1056px;overflow:hidden;' +
      'padding:64px 84px 48px}' +
      '.ldc-sheet *{visibility:visible !important}' +
      /* iOS Safari adds its own print margins on top of ours and does not
         honour @page at all, so the document is scaled to fit the paper
         WIDTH and the height has to have real slack or it tips onto a
         second, blank page ("Page 1 of 2" on her phone). Pricing measured
         the same overrun off her screenshots in r162. 1000px scales to
         about 9.2in of a 10in printable area; the flexible cake row
         absorbs the 56px and nothing else on the sheet moves. */
      '@supports(-webkit-touch-callout:none){' +
      'html,body{height:1000px}.ldc-sheet{height:1000px;padding-bottom:30px}}' +
      '</style></head><body>' +
      '<div class="ldc-sheet' + out.mods + '">' + out.html + '</div>' +
      '</body></html>';
  }

  /* PRINT FROM A DEDICATED DOCUMENT. r192.

     Printing the live page could not be made to work: on the theme it has
     a header, a footer, an announcement bar, its own @page margin, its own
     body spacing and (until this round) learn-serving.css's legacy
     `body * { visibility: hidden }`, which hid the sheet while the
     isolation rule hid everything else - a perfectly blank page. Every one
     of those is invisible in a local preview, because a preview has no
     theme. Pricing walked this same road for four rounds and ended in the
     same place (r148c, ldqPopupPrint).

     A popup owns its whole cascade: one stylesheet, one @page, no theme.
     And because .ldc-sheet's rules live OUTSIDE @media print, the popup
     gets them by simply LINKING the asset - no scraping document
     .styleSheets, which is what threw SecurityError on Shopify's
     cross-origin CDN in Pricing r178. Blocked popups fall back in place.

     Returns true when it has taken responsibility for printing.
     opts.noprint writes the document and hands back the window without
     opening a dialog - the QA door, same as Pricing's. */
  /* r194: iOS is not a small desktop, and the popup buys it NOTHING.

     The one thing a separate document wins is @page{margin:0}, which is
     what removes the browser's URL/date/page-number footer - and iOS
     Safari does not honour @page at all, so that footer prints either way.
     Against that it costs: a popup blocker that is ON by default, an
     about:blank document that will not wait for an external stylesheet
     (r193), and a second window she has to watch appear and disappear.

     In place, on iOS, the sheet's real stylesheet is ALREADY LOADED in the
     page - no fetch, no race, nothing to go missing. So iOS prints in
     place and the popup is a desktop optimisation. */
  function isIOS() {
    var ua = navigator.userAgent || '';
    var p = navigator.platform || '';
    return /iPhone|iPad|iPod/.test(ua) || /iP(hone|ad|od)/.test(p) ||
      /* iPadOS 13+ reports itself as a Mac; touch points give it away */
      (/Mac/.test(p) && (navigator.maxTouchPoints || 0) > 1);
  }

  function printSheet(opts) {
    var out = compose({ sample: !isPro() });
    if (!out) return false;

    if (isIOS() && !(opts && opts.forcePopup)) return inPagePrint();

    var css = planCssHref();
    /* opened SYNCHRONOUSLY, inside the click - iOS blocks a window opened
       out of a promise, however short. The document is written once the
       stylesheet text is in hand (normally already, prefetched at load). */
    var w = css ? openWin() : null;
    if (!w) return inPagePrint();

    cssReady().then(function () {
      /* the fetch failed, so the popup would print the sheet with none of
         its rules - a row of unstyled debris (r193, her phone). The page
         itself HAS the stylesheet; go back there rather than print that. */
      if (!cssText && !(opts && opts.noprint)) {
        try { w.close(); } catch (e) { }
        inPagePrint();
        return;
      }
      var doc = w.document;
      doc.open();
      doc.write(printDoc(out, css));
      doc.close();
      if (opts && opts.noprint) return;      /* the QA door */

      /* print once the webfonts are in: Marcellus arriving late reflows
         the count and the tier names, and a print snapshot taken before
         that gets the fallback serif. Belt and braces, because fonts.ready
         can hang on a blocked font host. */
      var done = false;
      function fire() {
        if (done) return;
        done = true;
        setTimeout(function () { try { w.focus(); w.print(); } catch (e) { } }, 250);
      }
      try { doc.fonts.ready.then(fire); } catch (e) { setTimeout(fire, 600); }
      setTimeout(fire, 1500);
      try {
        w.addEventListener('afterprint', function () {
          setTimeout(function () { try { w.close(); } catch (e) { } }, 300);
        });
      } catch (e) { }
    });
    return w && (opts && opts.noprint) ? w : true;
  }

  function openWin() {
    try { return window.open('', '_blank'); } catch (e) { return null; }
  }

  /* the fallback: arm the in-page sheet and print this document. Hardened
     against the theme (see the CSS), but it is the second choice. */
  function inPagePrint() {
    if (!build()) return false;
    try { window.print(); } catch (e) { return false; }
    return true;
  }

  /* ---------------------------------------------------------------- preview */

  function proUrl() {
    var mount = document.getElementById('ldcPlan');
    return ((mount && mount.getAttribute('data-pro-url')) || '').trim() ||
      '/products/learn-hub-pro';
  }

  function openPreview() {
    var out = compose({ sample: true });
    if (!out) return;

    var old = document.getElementById('ldcPlanOverlay');
    if (old) old.remove();

    var ov = document.createElement('div');
    ov.id = 'ldcPlanOverlay';
    ov.className = 'ldcp-overlay';
    ov.innerHTML =
      '<div class="ldcp-frame" role="dialog" aria-modal="true" aria-label="Serving plan preview">' +
        '<button type="button" class="ldcp-close" aria-label="Close preview">' +
          String.fromCharCode(215) + '</button>' +
        '<div class="ldcp-stage"></div>' +
        '<p class="ldcp-cta">This is the sheet LAYER' + String.fromCharCode(8217) +
          'D Toolkit Pro prints. Your studio name, your logo, your client' +
          String.fromCharCode(8217) + 's name on it, no watermark.' +
          '<br><a href="' + esc(proUrl()) + '" data-cta="toolkit">Unlock LAYER' +
          String.fromCharCode(8217) + 'D Toolkit Pro</a></p>' +
      '</div>';
    document.body.appendChild(ov);

    /* The scale is decided HERE, once, and baked into the sheet's own
       inline transform - nothing is measured after the write.

       It comes off window.innerWidth, NOT off the overlay. The overlay was
       appended a tick ago and clientWidth still reads a handful of pixels
       until the browser lays it out - measured on this very page it came
       back 5, which is Pricing's r178 bug exactly, and in a backgrounded
       tab documentElement.clientWidth reads 0 as well. innerWidth is a
       viewport property and needs no layout.

       GUTTER mirrors the CSS (the frame is min(860px, 100%) inside a 10px
       padding each side); SBAR is the overlay's own scrollbar, which
       overflow-y:scroll always reserves and innerWidth does not know
       about. Guessing it 16px wide costs at most a few px of sheet on a
       platform with overlay scrollbars, and .ldcp-stage is max-width:100%
       so an over-estimate can never push a horizontal bar onto the page. */
    var SHEET_W = 816, SHEET_H = 1056, GUTTER = 20, SBAR = 16;
    var avail = Math.min(860, (window.innerWidth || SHEET_W) - GUTTER - SBAR);
    var scale = Math.min(1, Math.max(0.2, avail / SHEET_W));

    var stage = ov.querySelector('.ldcp-stage');
    stage.style.width = Math.round(SHEET_W * scale) + 'px';
    stage.style.height = Math.round(SHEET_H * scale) + 'px';

    var sheet = document.createElement('div');
    sheet.className = 'ldc-sheet' + out.mods;
    sheet.style.transform = 'scale(' + scale + ')';
    sheet.innerHTML = out.html;
    stage.appendChild(sheet);

    function close() {
      ov.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape' || e.keyCode === 27) close(); }
    document.addEventListener('keydown', onKey);
    ov.addEventListener('click', function (e) { if (e.target === ov) close(); });
    ov.querySelector('.ldcp-close').addEventListener('click', close);
    ov.querySelector('.ldcp-cta a').addEventListener('click', function () {
      ga('learn_hub_cta_clicked', { cta_type: 'serving_plan_pro', feature: 'serving_plan_print' });
    });
    ov.querySelector('.ldcp-close').focus();

    ga('learn_hub_lock_viewed', { feature: 'serving_plan_print' });
  }

  /* ------------------------------------------------------------------- wire */

  adopt();
  /* warm the stylesheet text now, so the print document can go out complete
     in a single write the moment she taps Print (r193) */
  cssReady();

  /* ------------------------------------------------------------- ?plancheck
     r194: the printout has now failed three ways on a phone, and every time
     the next question was "what does the console say" - which on iOS Safari
     needs a Mac and a cable. So the diagnosis renders ON THE PAGE.

     Visit  /pages/serving-size-calculator?plancheck=1  , calculate a cake,
     and a panel names every link in the chain plus a button that shows the
     exact document the printer would get. Screenshot-able, no console, no
     desktop. Costs nothing when the parameter is absent. */
  function panel() {
    if (!/[?&]plancheck=1/.test(location.search)) return;
    if (document.getElementById('ldcPlanCheck')) return;

    var box = document.createElement('div');
    box.id = 'ldcPlanCheck';
    box.className = 'ldcp-check';
    document.body.appendChild(box);

    function row(ok, label, value) {
      return '<div class="ldcp-check__row"><b>' + (ok ? '✓' : '✕') +
        '</b><span>' + esc(label) + '</span><i>' + esc(value) + '</i></div>';
    }
    function paint() {
      var d = window.LAYERD.servingPlanPrint.diagnose();
      var bytes = d.planCssBytes;
      box.innerHTML =
        '<div class="ldcp-check__h">Serving plan check' +
          '<button type="button" id="ldcPlanCheckX" aria-label="Close">×</button></div>' +
        row(true, 'tier', d.tier) +
        row(d.mountFound, 'sheet mount', d.mountFound ? 'found' : 'MISSING - old template?') +
        row(d.mountIsBodyChild, 'mount on body', d.mountIsBodyChild ? 'yes' : 'NO') +
        row(d.corePresent, 'cake core', d.corePresent ? 'loaded' : 'MISSING') +
        row(d.planPublished, 'a cake to print', d.planPublished ? 'yes' : 'calculate one first') +
        row(!!d.composes, 'sheet composes', d.composes ? 'yes' : 'NO') +
        row(typeof bytes === 'number' && bytes > 1000, 'sheet styles',
            typeof bytes === 'number' ? bytes + ' bytes' : String(bytes)) +
        row(true, 'route', isIOS() ? 'in place (iOS)' : 'own document') +
        '<div class="ldcp-check__btns">' +
          '<button type="button" id="ldcPlanCheckShow">Show what would print</button>' +
          '<button type="button" id="ldcPlanCheckGo">Print it</button>' +
        '</div>';
      box.querySelector('#ldcPlanCheckX').onclick = function () { box.remove(); };
      box.querySelector('#ldcPlanCheckGo').onclick = function () { printSheet(); };
      box.querySelector('#ldcPlanCheckShow').onclick = showDoc;
    }

    /* the print document, on screen, in an iframe - what she would get on
       paper, without a print dialog and without a popup */
    function showDoc() {
      window.LAYERD.servingPlanPrint.printDoc().then(function (html) {
        if (!html) return;
        var ov = document.createElement('div');
        ov.className = 'ldcp-overlay';
        ov.innerHTML = '<div class="ldcp-frame">' +
          '<button type="button" class="ldcp-close" aria-label="Close">×</button>' +
          '<div class="ldcp-stage"><iframe class="ldcp-if" title="Print document"></iframe></div>' +
          '<p class="ldcp-cta">This is the document the printer gets. If it reads top to bottom, printing is fine and anything wrong is in the print dialog.</p>' +
        '</div>';
        document.body.appendChild(ov);
        var scale = Math.min(1, ((window.innerWidth || 816) - 36) / 816);
        var stage = ov.querySelector('.ldcp-stage');
        stage.style.width = Math.round(816 * scale) + 'px';
        stage.style.height = Math.round(1056 * scale) + 'px';
        var f = ov.querySelector('.ldcp-if');
        f.style.cssText = 'width:816px;height:1056px;border:0;transform:scale(' + scale + ');transform-origin:top left;background:#fff';
        var fd = f.contentDocument;
        fd.open(); fd.write(html); fd.close();
        ov.querySelector('.ldcp-close').onclick = function () { ov.remove(); };
        ov.addEventListener('click', function (e) { if (e.target === ov) ov.remove(); });
      });
    }
    paint();
    document.addEventListener('click', function () { setTimeout(paint, 400); }, true);
  }

  /* wire print triggers + expose the print API. (This tail was reconstructed on
     2026-09-07 from the deployed build after an editing slip truncated it.) */
  window.addEventListener('beforeprint', build);
  if (window.matchMedia) {
    var mq = window.matchMedia('print');
    var onMq = function (e) { if (e.matches) build(); };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  }
  var prevBtn = document.getElementById('ldcPlanPreviewBtn');
  if (prevBtn) prevBtn.addEventListener('click', openPreview);

  window.LAYERD = window.LAYERD || {};
  window.LAYERD.servingPlanPrint = {
    print: printSheet,
    build: build,
    preview: openPreview,
    compose: compose,
    printDoc: function () {
      return cssReady().then(function () {
        var out = compose({ sample: !isPro() });
        return out ? printDoc(out, planCssHref()) : '';
      });
    },
    diagnose: function () {
      var mount = document.getElementById('ldcPlan');
      var plan = window.LAYERD && window.LAYERD.servingPlan;
      return {
        tier: tier(),
        mountFound: !!mount,
        mountIsBodyChild: !!mount && mount.parentElement === document.body,
        planPublished: !!(plan && plan.tiers && plan.tiers.length),
        corePresent: !!core(),
        planCss: planCssHref() || '(NOT FOUND - popup print will fall back)',
        planCssBytes: cssText == null ? '(still fetching)' : cssText.length,
        composes: !!compose({ sample: !isPro() }),
        armed: document.body.classList.contains('ldc-plan-on')
      };
    }
  };
  panel();
})();